import React, { useState, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';

const BannerSection = () => {
  const navigate = useNavigate();
  const [hoveredSection, setHoveredSection] = useState(null);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1024);
  const hoverTimeoutRef = useRef(null);

  const handleMouseEnter = useCallback(
    section => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (isLargeScreen) {
        setHoveredSection(section);
      }
    },
    [isLargeScreen]
  );

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredSection(null);
    }, 150);
  }, []);

  const handleTAGClick = () => {
    const gallerySection = document.querySelector('[data-section="gallery"]');
    if (gallerySection) {
      const rect = gallerySection.getBoundingClientRect();
      const scrollTop = window.pageYOffset + rect.top - 40;
      window.scrollTo({ top: scrollTop, behavior: 'smooth' });
    }
  };

  const getBlueWidth = () => {
    if (!isLargeScreen) return '50%';
    if (hoveredSection === 'blue') return '70%';
    if (hoveredSection === 'purple') return '30%';
    return '50%';
  };

  const getPurpleWidth = () => {
    if (!isLargeScreen) return '50%';
    if (hoveredSection === 'blue') return '30%';
    if (hoveredSection === 'purple') return '70%';
    return '50%';
  };

  const handleKeyDown = (e, action) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  React.useEffect(() => {
    const handleResize = () => {
      const newIsLargeScreen = window.innerWidth >= 1024;
      setIsLargeScreen(newIsLargeScreen);
      if (!newIsLargeScreen) {
        setHoveredSection(null);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // 可复用的描边文字组件：使用双层叠加技术
  // 底层：纯黑色填充（color: 'black'）+ 2px 白色描边
  // 顶层：纯黑色填充，无描边（悬停时变为白色）
  // 绝对定位叠加：两层完美重叠，避免白线
  const OutlineText = ({ children, className = '', style = {} }) => (
    <div className={`relative ${className}`} style={style}>
      {/* 描边层 */}
      <h1
        className='absolute inset-0 font-black uppercase tracking-[-0.03em]'
        style={{
          fontSize: 'clamp(40px, 5vw, 64px)',
          fontFamily: 'Poppins, Inter, system-ui, sans-serif',
          color: 'black',
          WebkitTextStroke: '2px #ffffff',
          textStroke: '2px #ffffff',
        }}
        aria-hidden='true'
      >
        {children}
      </h1>
      {/* 填充层 - 悬停时变为白色 */}
      <h1
        className='relative font-black uppercase tracking-[-0.03em] text-black group-hover:text-white transition-colors duration-300'
        style={{
          fontSize: 'clamp(40px, 5vw, 64px)',
          fontFamily: 'Poppins, Inter, system-ui, sans-serif',
        }}
      >
        {children}
      </h1>
    </div>
  );

  return (
    <section
      className='relative min-h-[140px] lg:min-h-[160px] overflow-hidden'
      data-banner-section
    >
      <div className='flex flex-col lg:flex-row'>
        {/* 左栏 - TAG Gallery */}
        <div
          className='group relative w-full lg:w-1/2 bg-black hover:bg-gradient-to-b hover:from-blue-600 hover:to-blue-700 min-h-[140px] lg:min-h-[160px] flex items-center justify-center px-8 lg:px-8 py-8 cursor-pointer transition-all duration-500 ease-out'
          style={{
            width: isLargeScreen ? getBlueWidth() : '100%',
            transitionTimingFunction: 'cubic-bezier(.22,.61,.36,1)',
            minWidth: !isLargeScreen ? '100%' : 'auto',
          }}
          onClick={handleTAGClick}
          onKeyDown={e => handleKeyDown(e, handleTAGClick)}
          onMouseEnter={() => handleMouseEnter('blue')}
          onMouseLeave={handleMouseLeave}
          role='link'
          tabIndex={0}
        >
          <div className='flex flex-col items-center text-center'>
            <div className='leading-[0.8] mb-4'>
              <OutlineText>TAG</OutlineText>
              <h1
                className='text-white font-light tracking-[-0.03em]'
                style={{
                  fontSize: 'clamp(24px, 3vw, 40px)',
                  fontFamily: 'Poppins, Inter, system-ui, sans-serif',
                  textRendering: 'optimizeLegibility',
                }}
              >
                Gallery
              </h1>
            </div>
            <p
              className='text-white font-black leading-tight'
              style={{
                fontSize: 'clamp(24px, 3vw, 36px)',
              }}
            >
              SHOW YOUR WORK
            </p>
          </div>
        </div>

        {/* 右栏 - TAG Project */}
        <Link
          to='/tagme'
          className='group relative w-full lg:w-1/2 bg-black hover:bg-gradient-to-b hover:from-purple-600 hover:to-purple-700 min-h-[140px] lg:min-h-[160px] flex items-center justify-center px-8 lg:px-8 py-8 cursor-pointer transition-all duration-500 ease-out'
          style={{
            width: isLargeScreen ? getPurpleWidth() : '100%',
            transitionTimingFunction: 'cubic-bezier(.22,.61,.36,1)',
            minWidth: !isLargeScreen ? '100%' : 'auto',
          }}
          onMouseEnter={() => handleMouseEnter('purple')}
          onMouseLeave={handleMouseLeave}
        >
          <div className='flex flex-col items-center text-center'>
            <div className='leading-[0.8] mb-4'>
              <OutlineText>TAG</OutlineText>
              <h1
                className='text-white font-light tracking-[-0.03em]'
                style={{
                  fontSize: 'clamp(24px, 3vw, 40px)',
                  fontFamily: 'Poppins, Inter, system-ui, sans-serif',
                  textRendering: 'optimizeLegibility',
                }}
              >
                Project
              </h1>
            </div>
            <p
              className='text-white font-black leading-tight'
              style={{
                fontSize: 'clamp(24px, 3vw, 36px)',
              }}
            >
              FIND YOUR TEAM
            </p>
          </div>
        </Link>
      </div>
    </section>
  );
};

export default BannerSection;
